/**
 * The VOCABULARY of flight changes — what can change about a flight, and the
 * typed payload each kind carries. The envelope that transports these lives in
 * `FlightStatusReport.changes[]` (entities/flight_status); this file is only the
 * discriminated union and its enums, so that the producer (cirium), the store
 * (customer-api) and future renderers (traveler-panel timeline, genui card)
 * share ONE definition instead of copy-pasting it.
 *
 * The `from` side of a diff only exists at the producer: it is the previous
 * `flightSnapshots[i]` entry the cirium-integration holds before overwriting
 * it. Consumers can never reconstruct it, which is why it travels with the
 * change instead of being derived downstream. `from: null` means "first
 * observed value" — Cirium often announces a gate for the first time rather
 * than moving a known one.
 *
 * Operational instants (`scheduledAt`, `estimatedAt`, TIME_CHANGE from/to)
 * stay RAW strings, never `z.date()`: Cirium emits airport-LOCAL wall times
 * with the zone stripped, and coercing them to Date would stamp a wrong UTC
 * instant.
 */
import { z } from 'zod';

export const FlightChangeSideSchema = z.enum(['departure', 'arrival']);
export type FlightChangeSide = z.infer<typeof FlightChangeSideSchema>;

export const GateChangeSchema = z
  .object({
    changeType: z.literal('GATE_CHANGE'),
    side: FlightChangeSideSchema,
    from: z.string().min(1).nullable(),
    to: z.string().min(1),
  })
  .strict();

export const TerminalChangeSchema = z
  .object({
    changeType: z.literal('TERMINAL_CHANGE'),
    side: FlightChangeSideSchema,
    from: z.string().min(1).nullable(),
    to: z.string().min(1),
  })
  .strict();

export const FlightDelayedSchema = z
  .object({
    changeType: z.literal('FLIGHT_DELAYED'),
    side: FlightChangeSideSchema,
    delayMinutes: z.number().int().positive(),
    /** Airport-local wall time, raw string (see header). */
    scheduledAt: z.string().min(1).nullable(),
    estimatedAt: z.string().min(1).nullable(),
  })
  .strict();

export const TimeChangeSchema = z
  .object({
    changeType: z.literal('TIME_CHANGE'),
    side: FlightChangeSideSchema,
    from: z.string().min(1).nullable(),
    to: z.string().min(1),
  })
  .strict();

export const FlightCancelledSchema = z
  .object({ changeType: z.literal('FLIGHT_CANCELLED') })
  .strict();

export const FlightDivertedSchema = z
  .object({
    changeType: z.literal('FLIGHT_DIVERTED'),
    newAirport: z.string().min(1).nullable(),
  })
  .strict();

export const FlightReinstatedSchema = z
  .object({ changeType: z.literal('FLIGHT_REINSTATED') })
  .strict();

export const ConnectionAtRiskSchema = z
  .object({
    changeType: z.literal('CONNECTION_AT_RISK'),
    connectionTimeMinutes: z.number().int(),
    minimumConnectTimeMinutes: z.number().int(),
  })
  .strict();

export const FlightChangeDetailSchema = z.discriminatedUnion('changeType', [
  GateChangeSchema,
  TerminalChangeSchema,
  FlightDelayedSchema,
  TimeChangeSchema,
  FlightCancelledSchema,
  FlightDivertedSchema,
  FlightReinstatedSchema,
  ConnectionAtRiskSchema,
]);
export type FlightChangeDetail = z.infer<typeof FlightChangeDetailSchema>;

export const FLIGHT_CHANGE_TYPES = FlightChangeDetailSchema.options.map(
  (option) => option.shape.changeType.value,
);
export type FlightChangeType = FlightChangeDetail['changeType'];

/**
 * What the producer decided about contacting the traveler, stamped on a change
 * so the record answers "did we tell them?" without a later mutation:
 * `notified` = envelope published; `would_notify` = passed every filter but
 * notifications were off (shadow mode); `not_actionable` = informational type,
 * recorded but never a candidate for contact (e.g. gate changes).
 */
export const FlightChangeNotificationOutcomeSchema = z.enum([
  'notified',
  'would_notify',
  'not_actionable',
]);
export type FlightChangeNotificationOutcome = z.infer<typeof FlightChangeNotificationOutcomeSchema>;
